import { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import Ably from 'ably';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { addNewTicket, checkIfMessageIsForActiveTicket, updateTicket, updateTicketDataSync } from '@/store/slices/tickets/ticketsSlice';
import { addNewTicketToViews, bumpTicketToTopOfViews } from '@/store/slices/ticketPages/ticketPagesSlice';
import { keepUserActive, selectAgentStatuses, selectUserCurrentStatus } from '@/store/slices/agentStatuses/agentStatusesSlice';
import { useSelector } from 'react-redux';
import { updateConversationStatus } from '@/store/slices/conversations/conversationsSlice';
import { updateCustomer } from '@/store/slices/customers/customersSlice';
import { useAuth } from '@/context/AuthContext';
import { HttpClient } from '@/api/services/HttpClient';

interface AblyContextType {
    isConnected: boolean;
    subscribeToChannel: (channelName: string, eventName: string, callback: (message: any) => void) => Promise<void>;
    unsubscribeFromChannel: (channelName: string, eventName: string) => Promise<void>;
    publishToChannel: (channelName: string, eventName: string, data: Object) => Promise<void>;
}

const AblyContext = createContext<AblyContextType | null>(null);

interface AblyProviderProps {
    children: ReactNode;
}

export const AblyProvider = ({ children }: AblyProviderProps): JSX.Element => {
    const [realtime, setRealtime] = useState<Ably.Realtime | null>(null);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const activeChannelSubscriptions = new Map<string, Map<string, boolean>>();
    const { sessionData: user } = useAuth();
    const dispatch = useAppDispatch();
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const isUserLoggedIn = Boolean(user);

    useEffect(() => {
        audioRef.current = new Audio('https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3');
        audioRef.current.preload = 'auto';
    }, []);

    const userActivityInterval = useRef<NodeJS.Timeout | null>(null);
    const currentUserAgentStatus = useSelector(selectUserCurrentStatus);
    const agentStatuses = useSelector(selectAgentStatuses);

    // Keep pinging the server to keep the user active
    useEffect(() => {
        if (isUserLoggedIn) {
            userActivityInterval.current = setInterval(() => {
                if (currentUserAgentStatus) {
                    const status = agentStatuses.find(status => status.id === currentUserAgentStatus);
                    if (status?.isActiveStatus) {
                        void dispatch(keepUserActive());
                    }
                }
            }, 60000);

            // Cleanup interval on unmount
            return () => {
                if (userActivityInterval.current) {
                    clearInterval(userActivityInterval.current);
                }
            }
        }
    }, [currentUserAgentStatus, agentStatuses]);

    const connectToAbly = async (): Promise<Ably.Realtime> => {
        const authEndpoint = import.meta.env.VITE_REACT_APP_API_URL + '/ably/agentToken';

        const ablyClientOptions: Ably.ClientOptions = {
            authCallback: async (tokenParams, callback) => {
                try {
                    const response = await fetch(authEndpoint, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                    });

                    if (!response.ok) {
                        throw new Error(`Auth request failed: ${response.statusText}`);
                    }

                    // Assuming your backend returns the raw TokenRequest object or TokenDetails
                    const tokenRequestOrDetails = await response.json();
                    callback(null, tokenRequestOrDetails);
                } catch (err) {
                    callback(err as Ably.ErrorInfo, null);
                }
            },
            autoConnect: true,
            echoMessages: false,
            closeOnUnload: true,
            logLevel: 2,
            transports: ['web_socket', 'xhr_polling', 'comet'],
        };

        const realtime = new Ably.Realtime(ablyClientOptions);
        setRealtime(realtime);
        return realtime;
    };

    const handleTicketMessage = async (message: any) => {
        console.log("Ticket message", message);
        const ticket = message?.data;
        const currentUserId = user?.publicMetadata?.internalUserId as string;

        if (!ticket) {
            console.warn("No ticket ID or SNO found in message");
            return;
        }
        // Update/add ticket in the normalized store
        dispatch(updateTicket({ ...ticket, showMessageBadge: true }));

        // Bump ticket to top of appropriate views
        dispatch(bumpTicketToTopOfViews({
            ticket,
            currentUserId
        }));


        console.log(`Ticket ${ticket.sno || ticket.id} bumped to top due to new message`);
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(err => {
                console.warn('Failed to play notification sound:', err);
            });
        }
        // Send delivery reciept to customer
        // Channel name = dashboard:conversation:receipts
        // Event name = conversation_delivery_receipt
        const { conversationId } = ticket;

        if (conversationId) {
            publishToChannel("dashboard:conversation:receipts", "conversation_delivery_receipt", {
                conversationId
            })
        }

        dispatch(checkIfMessageIsForActiveTicket({
            ticketId: ticket.id,
            callback: () => {
                publishToChannel("dashboard:conversation:receipts", "conversation_read_receipt", {
                    conversationId: ticket.conversationId
                })
            }
        }));
    }


    const handleNewTicket = (message: any) => {
        console.log('New ticket received:', message);

        const ticket = message.data;
        const currentUserId = user?.publicMetadata?.internalUserId as string;

        // Add ticket to normalized store
        dispatch(addNewTicket(ticket));

        // Add ticket to appropriate views based on assignment
        dispatch(addNewTicketToViews({
            ticket,
            currentUserId
        }));
    };

    const handleCustomerDataUpdate = (message: any) => {
        console.log("Customer data updated", message);
        if (Object.keys(message?.data).length > 0) {
            dispatch(updateCustomer(message?.data));
        }
    }

    const handleNotificationEvents = (message: any) => {
        switch (message.name) {
            case "new_ticket":
                handleNewTicket(message);
                break;
            case "ticket_message":
                handleTicketMessage(message);
                break;
            case "ticket_update":
                console.log("Ticket updated", message);
                // handleTicketUpdate(message);
                break;
            default:
                break;
        }
    }

    const connectToDefaultChannels = async () => {
        if (user?.publicMetadata?.internalUserId && user?.publicMetadata?.clientId) {
            console.log(`Subscribing to notifications:client:${user.publicMetadata.clientId}`);
            subscribeToMultipleEvents(`notifications:client:${user.publicMetadata.clientId}`, ["new_ticket", "ticket_message", "ticket_update"], handleNotificationEvents);

            console.log(`Subscribing to data-sync:user:${user.publicMetadata.internalUserId}`);
            subscribeToMultipleEvents(`data-sync:user:${user.publicMetadata.internalUserId}`, ["customer_data_update", "ticket_data_update"], handleDataSyncEvents);

            console.log(`Subscribing to messages-channel:${user.publicMetadata.internalUserId}`);
            subscribeToChannel(`messages-channel:${user.publicMetadata.internalUserId}`, 'delivery_status', (message) => {
                handleMessageReciept(message?.data);
            });
        }
    }

    const handleTicketDataUpdate = (message: any) => {
        console.log("Ticket data updated", message);
        dispatch(updateTicketDataSync(message?.data));
    }

    const handleDataSyncEvents = (message: any) => {
        switch (message.name) {
            case "customer_data_update":
                handleCustomerDataUpdate(message);
                break;
            case "ticket_data_update":
                handleTicketDataUpdate(message);
                break;
        }
    }

    const handleMessageReciept = (message: any) => {
        console.log("Message receipt", message);
        const { dashboardGeneratedId, id, ticketId, status } = message;
        if (dashboardGeneratedId !== null) {
            dispatch(updateConversationStatus({
                ticketId,
                dashboardGeneratedId,
                status,
                id
            }));
        } else {
            dispatch(updateConversationStatus({
                ticketId,
                dashboardGeneratedId: null,
                status,
                id
            }));
        }
    }

    useEffect(() => {
        if (realtime) {
            realtime.connection.on('disconnected', () => {
                setIsConnected(false);
            });
            realtime.connection.on('connected', () => {
                setIsConnected(true);
                console.log('connected to ably');
                void connectToDefaultChannels();
            });
        }
    }, [realtime]);

    const subscribeToMultipleEvents = async (channelName: string, eventNames: string[], callback: (message: any) => void): Promise<void> => {
        if (!activeChannelSubscriptions.has(channelName)) {
            activeChannelSubscriptions.set(channelName, new Map());
        } else {
            console.log(`Channel ${channelName} already subscribed to event ${eventNames.join(', ')}`);
            return;
        }
        const channel = realtime?.channels.get(channelName);
        if (channel) {
            channel.subscribe(eventNames, callback);
        }
    };

    const subscribeToChannel = async (channelName: string, eventName: string, callback: (message: any) => void): Promise<void> => {
        if (!activeChannelSubscriptions.has(channelName)) {
            activeChannelSubscriptions.set(channelName, new Map());
        } else {
            console.log(`Channel ${channelName} already subscribed to event ${eventName}`);
            return;
        }
        const channel = realtime?.channels.get(channelName);
        if (channel) {
            const eventMap = activeChannelSubscriptions.get(channelName);
            if (!eventMap?.has(eventName)) {
                eventMap?.set(eventName, true);
                channel.subscribe(eventName, callback);
            }
        }
    };

    const unsubscribeFromChannel = async (channelName: string, eventName: string): Promise<void> => {
        const eventMap = activeChannelSubscriptions.get(channelName);
        if (eventMap?.has(eventName)) {
            eventMap.delete(eventName);
            const channel = realtime?.channels.get(channelName);
            if (channel) {
                channel.unsubscribe(eventName);
            }
            if (eventMap.size === 0) {
                activeChannelSubscriptions.delete(channelName);
            }

        }
    };

    const publishToChannel = async (channelName: string, eventName: string, data: Object): Promise<void> => {
        const channel = realtime?.channels.get(channelName);
        if (channel) {
            channel.publish(eventName, data);
        }
    };

    // Handle disconnetions and reconnections

    const contextValue: AblyContextType = {
        isConnected,
        subscribeToChannel,
        unsubscribeFromChannel,
        publishToChannel
    };

    useEffect(() => {
        if (isUserLoggedIn && !realtime) {
            void connectToAbly();

            return () => {
                if (realtime) {
                    realtime.close();
                }
            }
        }
    }, []);

    if (!realtime && isUserLoggedIn) {
        return <div>Loading...</div>;
    }

    return (
        <AblyContext.Provider value={contextValue}>
            {children}
        </AblyContext.Provider>
    );
};

export const useAblyContext = () => {
    const context = useContext(AblyContext);
    if (!context) {
        throw new Error('useAblyContext must be used within an AblyProvider');
    }
    return context;
};

export { AblyContext };
